import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/registry/reapit/lib/utils'

/*
 * Status Indicator — DS component (Phase 3C)
 *
 * DS Page: 8403139
 *
 * A compact, inline element: a coloured dot + a short text label.
 * Non-interactive by default. Used to communicate status in tables,
 * list rows, cards, or next to avatars.
 *
 * Semantic variants (DS-defined):
 *   neutral   — #9FAEBC (neutral-300) — unknown / not set states
 *   success   — #1AA135 (green-600)   — online, active, synced
 *   pending   — #D68600 (yellow-600)  — in progress, queued, scheduled
 *   warning   — #FF6000 (orange-500)  — expiring soon, unstable
 *   danger    — #F01830 (red-500)     — failed, disconnected
 *   inactive  — #607890 (neutral-500) — offline, disabled, archived
 *   accent-1  — #0096AA (teal-600)    — auxiliary custom states
 *   accent-2  — #9A32B4 (plum-500)    — auxiliary custom states
 *
 * Dot size: size-2 (8px) — compact, pairs well with DS text-sm label.
 * All colours reference DS semantic icon/text tokens from Phase 1.
 */

const dotVariants = cva('size-2 shrink-0 rounded-full', {
  variants: {
    variant: {
      neutral: 'bg-icon-primary' /* neutral-300 #9FAEBC */,
      success: 'bg-icon-success' /* green-600   #1AA135 */,
      pending: 'bg-icon-pending' /* yellow-600  #D68600 */,
      warning: 'bg-icon-warning' /* orange-500  #FF6000 */,
      danger: 'bg-icon-error' /* red-500      #F01830 */,
      inactive: 'bg-icon-secondary' /* neutral-500 #607890 */,
      'accent-1': 'bg-icon-accent-1' /* teal-600    #0096AA */,
      'accent-2': 'bg-icon-accent-2' /* plum-500    #9A32B4 */,
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
})

const labelVariants = cva('text-sm', {
  variants: {
    variant: {
      neutral: 'text-muted-foreground',
      success: 'text-text-success',
      pending: 'text-text-pending',
      warning: 'text-text-warning',
      danger: 'text-text-error',
      inactive: 'text-muted-foreground',
      'accent-1': 'text-text-accent-1',
      'accent-2': 'text-text-accent-2',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
})

type StatusIndicatorVariant = VariantProps<typeof dotVariants>['variant']

type StatusIndicatorProps = React.ComponentProps<'span'> & {
  /** Semantic status variant — drives dot and label colour. */
  variant?: StatusIndicatorVariant
  /**
   * Short label text (1–2 words). Always required for accessibility —
   * do not rely on colour alone to communicate status.
   */
  label: string
  /** When true, the label is visually hidden but still announced by screen readers. */
  labelHidden?: boolean
}

// Expose as a namespace for consistency with the project convention.
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace StatusIndicator {
  export type Props = StatusIndicatorProps
  export type Variant = StatusIndicatorVariant
}

function StatusIndicator({
  variant = 'neutral',
  label,
  labelHidden = false,
  className,
  ...props
}: StatusIndicatorProps) {
  return (
    <span
      data-slot="status-indicator"
      data-variant={variant}
      className={cn('inline-flex items-center gap-1.5', className)}
      {...props}
    >
      <span data-slot="status-indicator-dot" aria-hidden="true" className={cn(dotVariants({ variant }))} />
      <span data-slot="status-indicator-label" className={cn(labelVariants({ variant }), labelHidden && 'sr-only')}>
        {label}
      </span>
    </span>
  )
}

export { StatusIndicator }
